import {
  ByCategoryBrandContentElementContentModel,
  ByCategoryBrandContentPropertiesModel,
} from '@/services/platform/api/model';
import { cn, LinksToLinkMap, ModuleProperties, ImagesToImageMap } from '@/lib/core';
import ModuleWrapper from '@/components/shared/module/wrapper';
import { ShopDiscoverBanner } from '@/components/shared/shop';
import { Link } from '@/lib/core/utils/links';

export const ByCategoryBrandContent = (props: ModuleProperties) => {
  const content = props.content as ByCategoryBrandContentElementContentModel | undefined;
  const properties = content?.properties as ByCategoryBrandContentPropertiesModel;
  // No `useLocaleStore` here: it was only read by a condition that never used it. The banner renders
  // entirely from `content`, which `Screen.tsx` already re-fetches per locale.

  if (!content) return null;

  const bannerTitle = properties?.title;
  const bannerDescription = properties?.description;
  /*
   * The section id is the whole point of the link, so it decides whether there is a link at all.
   *
   * Interpolating it unchecked produced `/brands_undefined` for any module whose `section` picker was
   * left empty, and the synthesized fallback below was no better — it hardcoded a bare `/brands_`
   * with nothing after it. Both are links that look real and resolve to nothing.
   */
  const sectionId = properties?.section?.[0]?.id;
  const brandsHref = sectionId ? `${props?.context?.page?.route?.path}/brands_${sectionId}` : undefined;
  const bannerLink =
    LinksToLinkMap(properties?.link ?? undefined) ??
    (brandsHref ? ({ href: brandsHref, label: bannerTitle ?? '' } as Link) : undefined);
  const bannerImage = ImagesToImageMap(properties?.image);

  // `locale` was in this condition but never used in the href it builds; the id is what matters.
  if (bannerLink && brandsHref) {
    bannerLink.href = brandsHref;
  }

  return (
    <ModuleWrapper
      component={props}
      classNames={{
        // See byCategoryCategoryContent/index.tsx for what `group` and the `last:mb-[...]`
        // override do — same mechanism, shared by every ShopDiscoverBanner-rendering module.
        base: cn([
          'module__by-category-category-content group mb-1.25',
          'last:mb-[calc(0.3125rem-max(1rem,env(safe-area-inset-bottom)))]!',
        ]),
        container: cn(['flex-1']),
        wrapper: cn(['py-0', 'flex-1']),
        grid: cn(['flex flex-col flex-1 gap-0']),
      }}
      isFullWidth={true}
      {...props}
    >
      <ShopDiscoverBanner
        image={bannerImage}
        title={bannerTitle}
        description={bannerDescription}
        link={bannerLink}
      />
    </ModuleWrapper>
  );
};
